// src/shell/theme.js — теми (светла / тъмна / синьо-сива). Пренесено от app.js.
// Прилага темата ОЩЕ при import (преди първия рендер).

import { el, field } from '../shared/dom.js';
import { state } from '../shared/state.js';
import { PALETTES, ensurePaletteStyle } from './palettes.js';

function preferenceKey(name) {
  // На споделен компютър изборът на един служител не сменя този на другия.
  let user; try { user = state().user; } catch (_) {}
  return user && user.id ? name + ':user:' + user.id : name;
}
function readPreference(name, fallback) { try { return localStorage.getItem(preferenceKey(name)) || localStorage.getItem(name) || fallback; } catch (_) { return fallback; } }
export function currentTheme() { const t = readPreference('theme', 'light'); return ['light', 'dark', 'bluegray', 'auto'].includes(t) ? t : 'light'; }
export function currentPalette() { const p = readPreference('palette', currentTheme() === 'bluegray' ? 'blue' : 'green'); return Object.hasOwn(PALETTES, p) ? p : 'green'; }
const systemTheme = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
export function syncAppearance() {
  const t = currentTheme();
  document.documentElement.setAttribute('data-theme', t === 'dark' || (t === 'auto' && systemTheme && systemTheme.matches) ? 'dark' : '');
  document.documentElement.setAttribute('data-palette', currentPalette());
  document.documentElement.style.colorScheme = document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}
export function applyPalette(p) {
  if (!Object.hasOwn(PALETTES, p)) p = 'green';
  try { localStorage.setItem(preferenceKey('palette'), p); } catch (_) {}
  syncAppearance();
}

export function applyTheme(t) {
  if (!['light', 'dark', 'bluegray', 'auto'].includes(t)) t = 'light';
  try { localStorage.setItem(preferenceKey('theme'), t); } catch (e) {}
  syncAppearance();
}

function ensureThemeStyle() {
  if (document.getElementById('theme-css')) return;
  const css = [
    '[data-theme="dark"]{',
    // --faint беше #6b7a93: 3,88:1 върху --surface и 3,50:1 върху --surface-2,
    // а се ползва за ТЕКСТ (заглавия на колони, подтекст в картите, стрелки).
    // #94a3b8 дава 6,58:1 / 5,93:1 — минава 4,5:1 по WCAG 2.1 AA.
    '  --bg:#0b1220; --surface:#141d2e; --surface-2:#1b2638; --ink:#e6edf6; --muted:#9aa7bd; --faint:#94a3b8; --border:#2a3650;',
    '  --accent:#14b8a6; --accent-hover:#0d9488; --accent-soft:#0e3b37;',
    '  --green:#4ade80; --green-soft:#10331f; --amber:#fbbf24; --amber-soft:#3a2c0a; --red:#f87171; --red-soft:#3a1414; --blue:#60a5fa; --blue-soft:#152544; --purple:#c4b5fd; --purple-soft:#2a2150; --teal:#5eead4; --teal-soft:#0d3b36;',
    '  --shadow:0 1px 2px rgba(0,0,0,.45), 0 6px 22px rgba(0,0,0,.4); color-scheme: dark;',
    '}',
    // В тъмната тема --accent е СВЕТЪЛ тюркоаз, а .btn пише с бяло върху него:
    // 2,49:1 — най-лошият контраст в цялото приложение, и то на главния бутон
    // („Запази", „+ Нов контакт"). Тъмен надпис върху светлия акцент дава 7,52:1
    // и остава 5,00:1 при посочване (--accent-hover).
    '[data-theme="dark"] .btn{ color:#0b1220; }',
    '[data-theme="dark"] .btn.ghost{ color:var(--ink); }',
    // „Отказ" не се виждаше на тъмния фон (Макс, 16.10.2026, т. 3к): рамката
    // беше --border (#2a3650) върху --surface-2 — 1,3:1, бутонът се сливаше с
    // долната лента и изглеждаше като голи букви. --faint дава 5,9:1.
    '[data-theme="dark"] .btn.ghost, [data-theme="dark"] .btn.danger{ border-color:var(--faint); }',
    '[data-theme="dark"] .btn.danger{ color:var(--red); }',
    '[data-theme="bluegray"]{',
    // Тук беше най-зле: #8392a8 върху светъл фон дава едва 2,99:1.
    // #5a6b85 дава 5,13:1 / 4,68:1.
    '  --bg:#e6ecf4; --surface:#f6f9fc; --surface-2:#e9eff7; --ink:#1f2a3d; --muted:#55667e; --faint:#5a6b85; --border:#ccd7e6;',
    // --blue беше #2563eb: върху --blue-soft дава 4,12:1 (етикетчето „✓ Данни").
    // #1d4ed8 дава 5,34:1.
    '  --accent:#3a5a8c; --accent-hover:#2f4a76; --accent-soft:#e0e9f6; --blue:#1d4ed8; --blue-soft:#dbe6fb; --purple:#5b3fa8; --purple-soft:#e6dff5; --teal:#0f766e; --teal-soft:#d5f5f0;',
    '  --shadow:0 1px 2px rgba(30,42,61,.08), 0 4px 16px rgba(30,42,61,.07);',
    '}',
    '.btn{ transition: background .15s ease, box-shadow .15s ease, transform .05s ease; }',
    '.btn:active{ transform: translateY(1px); }',
    'input:focus, select:focus, textarea:focus{ outline: 2px solid var(--accent-soft); outline-offset: 1px; border-color: var(--accent); }',
    'tbody tr:hover{ background: var(--surface-2); }',
    // При печат фонът става бял — --faint също трябва да потъмнее, иначе
    // светлият вариант от тъмната тема става нечетим на хартия.
    '@media print { [data-theme="dark"], [data-theme="bluegray"]{ --bg:#fff; --surface:#fff; --surface-2:#fff; --ink:#0f172a; --muted:#475569; --faint:#475569; --border:#e4e8ee; } }',
  ].join('\n');
  const st = document.createElement('style'); st.id = 'theme-css'; st.textContent = css;
  (document.head || document.documentElement).appendChild(st);
}

try { ensureThemeStyle(); ensurePaletteStyle(); syncAppearance(); } catch (e) {}
if (systemTheme && systemTheme.addEventListener) systemTheme.addEventListener('change', () => { if (currentTheme() === 'auto') syncAppearance(); });

export function themeSwitch() {
  const cur = currentTheme() === 'bluegray' ? 'light' : currentTheme();
  const palette = el('select', { onChange: e => { applyPalette(e.target.value); } }, ...Object.entries(PALETTES).map(([id, label]) => el('option', { value: id, text: label, selected: currentPalette() === id })));
  const mode = el('select', { onChange: e => { applyTheme(e.target.value); } }, ...[['light', 'Светъл'], ['dark', 'Тъмен'], ['auto', 'Автоматично']].map(([id, label]) => el('option', { value: id, text: label, selected: cur === id })));
  return el('div', { class: 'appearance-settings', onClick: e => e.stopPropagation() }, field('Цветова тема', palette), field('Режим', mode));
}
